import React from 'react';

import type { IconName } from '@/shared/icons';

import { Button } from './Button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface CustomizeMenuItem<T extends string = string> {
  id: T;
  label: string;
  /** Título de grupo no menu (itens do mesmo grupo devem vir juntos em `items`). */
  group?: string;
}

export interface CustomizeMenuProps<T extends string = string> {
  /** Todos os itens disponíveis, NA ORDEM de exibição. */
  items: Array<CustomizeMenuItem<T>>;
  /** Itens marcados. */
  selected: T[];
  /** Recebe os marcados já na ordem de `items`. */
  onChange: (ids: T[]) => void;
  /** Volta a este conjunto em "Restaurar padrão". */
  defaults: T[];
  /** Rótulo do botão. Padrão: "Personalizar". */
  label?: string;
  icon?: IconName;
  /** Título no topo do menu (ex: "Colunas visíveis"). */
  title?: string;
  'data-testid': string;
}

/**
 * Menu de "escolha o que aparece" — colunas de tabela, blocos de um painel.
 *
 * Marcar/desmarcar aplica na hora e o menu continua aberto, para escolher
 * vários de uma vez; fecha ao clicar fora ou com Esc (superfície transiente).
 * A ORDEM é sempre a de `items`: o usuário escolhe o quê, não onde.
 */
export const CustomizeMenu = <T extends string>({
  items,
  selected,
  onChange,
  defaults,
  label = 'Personalizar',
  icon = 'sliders',
  title,
  'data-testid': testId,
}: CustomizeMenuProps<T>): React.ReactElement => {
  const selectedSet = new Set(selected);

  const toggle = (id: T, checked: boolean): void => {
    const next = new Set(selectedSet);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(items.filter((item) => next.has(item.id)).map((item) => item.id));
  };

  const isDefault =
    selected.length === defaults.length && defaults.every((id) => selectedSet.has(id));

  let lastGroup: string | undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" icon={icon} data-testid={testId}>
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="z-[100] w-64 max-h-[70vh] overflow-y-auto"
        data-testid={`${testId}-menu`}
      >
        {title && <DropdownMenuLabel>{title}</DropdownMenuLabel>}
        {items.map((item) => {
          const showGroup = item.group !== undefined && item.group !== lastGroup;
          lastGroup = item.group;
          return (
            <React.Fragment key={item.id}>
              {showGroup && (
                <DropdownMenuLabel className="pt-2.5">{item.group}</DropdownMenuLabel>
              )}
              <DropdownMenuCheckboxItem
                checked={selectedSet.has(item.id)}
                onCheckedChange={(checked) => toggle(item.id, checked === true)}
                // Sem isto o Radix fecha o menu a cada clique.
                onSelect={(e) => e.preventDefault()}
                data-testid={`${testId}-item-${item.id}`}
              >
                {item.label}
              </DropdownMenuCheckboxItem>
            </React.Fragment>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={isDefault}
          onSelect={() => onChange(items.filter((i) => defaults.includes(i.id)).map((i) => i.id))}
          data-testid={`${testId}-reset`}
        >
          Restaurar padrão
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
